import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const ProgramCasesPage = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [program, setProgram] = useState(null);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: programData, error: programErr } = await supabase
          .from('programs')
          .select('id, name, university_id, universities ( name )')
          .eq('id', programId)
          .single();
        if (programErr) throw programErr;
        setProgram(programData);

        const { data: apps, error: appsErr } = await supabase
          .from('uni_applications')
          .select(`
            id,
            status,
            metadata,
            client_id,
            case_id,
            cases ( id, title, client_id ),
            clients:client_id ( first_name, last_name, primary_email ),
            intakes ( term, apply_close )
          `)
          .eq('program_id', programId);
        if (appsErr) throw appsErr;
        setApplications(apps || []);
      } catch (err) {
        setError(err.message || 'Failed to load program cases');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [programId]);

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Program Cases</h1>
          <p className="text-sm text-gray-400">
            {program
              ? `${program.universities?.name || 'University'} • ${program.name}`
              : 'Loading program info…'}
          </p>
        </div>
        <button
          className="px-3 py-1 text-sm border border-gray-700 rounded bg-gray-800 hover:border-blue-500"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {loading && <div className="text-sm text-gray-400">Loading program cases…</div>}
      {error && <div className="text-sm text-red-400">Error: {error}</div>}

      {!loading && !error && (
        <div className="bg-gray-800 border border-gray-700 rounded overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
              <tr>
                <th className="p-3 text-left">Application</th>
                <th className="p-3 text-left">Case</th>
                <th className="p-3 text-left">Client</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Deadline</th>
                <th className="p-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {applications.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">
                    No applications found for this program.
                  </td>
                </tr>
              )}
              {applications.map((app) => {
                const deadline =
                  app.metadata?.deadline ||
                  app.intakes?.apply_close ||
                  app.metadata?.apply_close ||
                  null;
                const clientName = [app.clients?.first_name, app.clients?.last_name].filter(Boolean).join(' ') || 'Client';
                return (
                  <tr key={app.id} className="hover:bg-gray-700/50">
                    <td className="p-3 font-medium text-white">
                      {app.id}
                      <div className="text-xs text-gray-500 mt-1">Uni Application</div>
                    </td>
                    <td className="p-3 text-gray-300">
                      {app.cases?.title || '—'}
                    </td>
                    <td className="p-3 text-gray-300">
                      {clientName}
                      {app.clients?.primary_email && (
                        <div className="text-xs text-gray-500">{app.clients.primary_email}</div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded text-xs bg-gray-900 border border-gray-700 uppercase">
                        {app.status || '—'}
                      </span>
                    </td>
                    <td className="p-3 text-yellow-300">
                      {deadline ? new Date(deadline).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          className="px-2 py-1 text-xs bg-blue-600 rounded hover:bg-blue-700"
                          onClick={() => navigate(`/admin/univ_apps/${app.id}`)}
                        >
                          Application
                        </button>
                        {app.case_id && (
                          <button
                            className="px-2 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600"
                            onClick={() => navigate(`/admin/cases/${app.case_id}`)}
                          >
                            Case
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProgramCasesPage;
